import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

import ptBRLocale from 'date-fns/locale/pt-BR';
import { isSameDay, parseISO, format } from 'date-fns';
import DateFnsUtils from '@date-io/date-fns';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import Backdrop from '~/components/BackdropComponent';
import CircularProgress from '@material-ui/core/CircularProgress';
import BloodItem from './components/BloodItem';
import BloodShow from './components/BloodShow';
import { errorMessage } from '~/utils/Message';

import { createTheme, ThemeProvider } from '@material-ui/core/styles';
import { calendarBloods, calendarShow } from '~/services/bloods';

import {
  Container,
  Content,
  Tag,
  Subtitle,
  List,
} from '../InfusionCalendar/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#0384ac',
    },
  },
});

function BloodCalendar({ user }) {
  const [bloods, setBloods] = useState([]);
  const [dates, setDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(false);

  const [active, setActive] = useState(null);
  const { id } = user;

  const loadData = useCallback(
    async (date) => {
      setBloods([]);
      setDates([]);
      setLoading(true);

      try {
        const response = await calendarBloods({ date, userId: id });

        setBloods(response.bloods);
        setDates(response.dates);
      } catch (error) {
        errorMessage(error);
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  const handleSelectedDate = useCallback(
    async (date) => {
      setBloods([]);
      setLoading(true);

      try {
        const response = await calendarShow({
          date: format(new Date(date), 'yyyy-MM-dd'),
          userId: id,
        });

        setBloods(response.bloods);
      } finally {
        setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    loadData(new Date());
  }, [loadData]);

  useEffect(() => {
    if (!selectedDate) return;

    handleSelectedDate(selectedDate);
  }, [selectedDate, handleSelectedDate]);

  return (
    <Container>
      <Content>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBRLocale}>
          <ThemeProvider theme={theme}>
            <DatePicker
              loadingIndicator={<span />}
              value={selectedDate}
              onChange={setSelectedDate}
              onMonthChange={loadData}
              orientation="landscape"
              variant="static"
              openTo="date"
              renderDay={(
                day,
                selectedDate,
                isInCurrentMonth,
                dayComponent
              ) => {
                const active = dates.some((i) => isSameDay(day, parseISO(i)));

                // You can also use our internal <Day /> component
                return active && isInCurrentMonth ? (
                  <Tag>
                    {dayComponent} <span id="tag" />
                  </Tag>
                ) : (
                  dayComponent
                );
              }}
            />
          </ThemeProvider>
        </MuiPickersUtilsProvider>

        <Subtitle style={{ marginBottom: !bloods.length && 14 }}>
          Registro de sangramento
        </Subtitle>

        <List>
          {bloods.map((item, index) => (
            <BloodItem item={item} key={index} setActive={setActive} />
          ))}
        </List>
      </Content>

      {active && <BloodShow item={active} />}

      <Backdrop open={loading}>
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
}

BloodCalendar.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    register: PropTypes.number,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
  }).isRequired,
};

export default BloodCalendar;
